import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Box, Button, Link, Paper, Stack, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, TextField,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AccountingService } from '../../services/accounting.service';
import { useToast } from '../../context/ToastContext';
import {
    IAccount, IBalanceSheet, ILedger, IProfitLoss, IReportRow, ITrialBalance,
} from '../../interfaces/accounting.interface';
import AccountSelect from './AccountSelect';
import { downloadCsv, toCsv } from './csv';
import { firstOfMonth, formatMoney, today } from './money';
import { useAccountingInstitution } from './useAccountingInstitution';

type TabKey = 'trial' | 'pl' | 'bs' | 'ledger';

const DateField: React.FC<{ id: string; label: string; value: string; onChange: (v: string) => void }> =
    ({ id, label, value, onChange }) => (
        <TextField id={id} size="small" type="date" label={label} value={value}
                   onChange={e => onChange(e.target.value)} InputLabelProps={{ shrink: true }} />
    );

const Section: React.FC<{ title: string; rows: IReportRow[]; total: number; totalLabel: string }> =
    ({ title, rows, total, totalLabel }) => (
        <>
            <TableRow><TableCell colSpan={2}><strong>{title}</strong></TableCell></TableRow>
            {rows.map(r => (
                <TableRow key={r.accountId}>
                    <TableCell sx={{ pl: 4 }}>{r.code} · {r.name}</TableCell>
                    <TableCell align="right">{formatMoney(r.amount)}</TableCell>
                </TableRow>
            ))}
            <TableRow>
                <TableCell><strong>{totalLabel}</strong></TableCell>
                <TableCell align="right"><strong>{formatMoney(total)}</strong></TableCell>
            </TableRow>
        </>
    );

const rowsCsv = (section: string, rows: IReportRow[]) => rows.map(r => [section, r.code, r.name, r.amount]);

/** Trial balance, profit & loss, balance sheet and account ledger, each exportable to CSV. */
const AccountingReports: React.FC = () => {
    const institutionId = useAccountingInstitution();
    const { t } = useTranslation(['accounting', 'common']);
    const { showApiError } = useToast();
    const [tab, setTab] = useState<TabKey>('trial');
    const [asOf, setAsOf] = useState(today());
    const [from, setFrom] = useState(firstOfMonth());
    const [to, setTo] = useState(today());
    const [accountId, setAccountId] = useState('');
    const [accounts, setAccounts] = useState<IAccount[]>([]);
    const [trial, setTrial] = useState<ITrialBalance | null>(null);
    const [pl, setPl] = useState<IProfitLoss | null>(null);
    const [bs, setBs] = useState<IBalanceSheet | null>(null);
    const [ledger, setLedger] = useState<ILedger | null>(null);

    useEffect(() => {
        if (!institutionId) return;
        AccountingService.listAccounts(institutionId)
            .then(setAccounts)
            .catch(error => showApiError(error, t('errors.loadAccounts')));
    }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!institutionId) return;
        const fail = (error: unknown) => showApiError(error, t('reports.loadFailed'));
        if (tab === 'trial' && asOf) AccountingService.trialBalance(institutionId, asOf).then(setTrial).catch(fail);
        if (tab === 'pl' && from && to) AccountingService.profitLoss(institutionId, from, to).then(setPl).catch(fail);
        if (tab === 'bs' && asOf) AccountingService.balanceSheet(institutionId, asOf).then(setBs).catch(fail);
        if (tab === 'ledger' && accountId && from && to) {
            AccountingService.ledger(institutionId, accountId, from, to).then(setLedger).catch(fail);
        }
    }, [institutionId, tab, asOf, from, to, accountId]); // eslint-disable-line react-hooks/exhaustive-deps

    const exportCsv = () => {
        if (tab === 'trial' && trial) {
            downloadCsv(`trial-balance-${trial.asOf}.csv`, toCsv(['Code', 'Account', 'Debit', 'Credit'], [
                ...trial.rows.map(r => [r.code, r.name, r.debit, r.credit]),
                ['', 'Total', trial.totalDebit, trial.totalCredit],
            ]));
        } else if (tab === 'pl' && pl) {
            downloadCsv(`profit-loss-${pl.from}-to-${pl.to}.csv`, toCsv(['Section', 'Code', 'Account', 'Amount'], [
                ...rowsCsv('Income', pl.income),
                ...rowsCsv('Expenses', pl.expenses),
                ['Net profit', '', '', pl.netProfit],
            ]));
        } else if (tab === 'bs' && bs) {
            downloadCsv(`balance-sheet-${bs.asOf}.csv`, toCsv(['Section', 'Code', 'Account', 'Amount'], [
                ...rowsCsv('Assets', bs.assets),
                ...rowsCsv('Liabilities', bs.liabilities),
                ...rowsCsv('Equity', bs.equity),
                ['Equity', '', 'Current earnings', bs.currentEarnings],
                ['Total assets', '', '', bs.totalAssets],
                ['Total liabilities and equity', '', '', bs.totalLiabilitiesAndEquity],
            ]));
        } else if (tab === 'ledger' && ledger) {
            downloadCsv(`ledger-${ledger.code}-${ledger.from}-to-${ledger.to}.csv`,
                toCsv(['Date', 'Entry', 'Description', 'Debit', 'Credit', 'Balance'], [
                    ['', '', 'Opening balance', '', '', ledger.openingBalance],
                    ...ledger.rows.map(r => [r.date, r.entryNumber, r.memo || r.description, r.debit, r.credit, r.runningBalance]),
                    ['', '', 'Closing balance', '', '', ledger.closingBalance],
                ]));
        }
    };

    return (
        <Box sx={{ p: 3, maxWidth: 1000 }}>
            <Typography variant="h5" sx={{ mb: 1 }}>{t('reports.title')}</Typography>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable">
                <Tab value="trial" label={t('reports.tabs.trial')} />
                <Tab value="pl" label={t('reports.tabs.pl')} />
                <Tab value="bs" label={t('reports.tabs.bs')} />
                <Tab value="ledger" label={t('reports.tabs.ledger')} />
            </Tabs>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems={{ sm: 'center' }}>
                {(tab === 'trial' || tab === 'bs') && <DateField id="report-as-of" label={t('fields.asOf')} value={asOf} onChange={setAsOf} />}
                {tab === 'ledger' && (
                    <Box sx={{ minWidth: 260 }}>
                        <AccountSelect id="report-account" label={t('fields.account')} value={accountId}
                                       accounts={accounts} onChange={setAccountId} />
                    </Box>
                )}
                {(tab === 'pl' || tab === 'ledger') && (
                    <>
                        <DateField id="report-from" label={t('fields.from')} value={from} onChange={setFrom} />
                        <DateField id="report-to" label={t('fields.to')} value={to} onChange={setTo} />
                    </>
                )}
                <Box sx={{ flexGrow: 1 }} />
                <Button variant="outlined" onClick={exportCsv}>{t('reports.exportCsv')}</Button>
            </Stack>

            <Paper variant="outlined">
                {tab === 'trial' && trial && (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('fields.account')}</TableCell>
                                <TableCell align="right">{t('fields.debit')}</TableCell>
                                <TableCell align="right">{t('fields.credit')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {trial.rows.map(r => (
                                <TableRow key={r.accountId}>
                                    <TableCell>{r.code} · <span>{r.name}</span></TableCell>
                                    <TableCell align="right">{r.debit ? formatMoney(r.debit) : ''}</TableCell>
                                    <TableCell align="right">{r.credit ? formatMoney(r.credit) : ''}</TableCell>
                                </TableRow>
                            ))}
                            <TableRow>
                                <TableCell><strong>{t('common:fields.total')}</strong></TableCell>
                                <TableCell align="right"><strong>{formatMoney(trial.totalDebit)}</strong></TableCell>
                                <TableCell align="right"><strong>{formatMoney(trial.totalCredit)}</strong></TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                )}
                {tab === 'pl' && pl && (
                    <Table size="small">
                        <TableBody>
                            <Section title={t('accountTypes.INCOME')} rows={pl.income} total={pl.totalIncome} totalLabel={t('reports.totalIncome')} />
                            <Section title={t('accountTypes.EXPENSE')} rows={pl.expenses} total={pl.totalExpense} totalLabel={t('reports.totalExpenses')} />
                            <TableRow>
                                <TableCell><strong>{pl.netProfit < 0 ? t('reports.netLoss') : t('reports.netProfit')}</strong></TableCell>
                                <TableCell align="right"><strong>{formatMoney(pl.netProfit)}</strong></TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                )}
                {tab === 'bs' && bs && (
                    <Table size="small">
                        <TableBody>
                            <Section title={t('accountTypes.ASSET')} rows={bs.assets} total={bs.totalAssets} totalLabel={t('reports.totalAssets')} />
                            <Section title={t('accountTypes.LIABILITY')} rows={bs.liabilities} total={bs.totalLiabilities} totalLabel={t('reports.totalLiabilities')} />
                            <Section title={t('accountTypes.EQUITY')} rows={[...bs.equity, {
                                accountId: 'current-earnings', code: '—', name: t('reports.currentEarnings'), type: 'EQUITY',
                                amount: bs.currentEarnings,
                            }]} total={bs.totalEquity} totalLabel={t('reports.totalEquity')} />
                            <TableRow>
                                <TableCell><strong>{t('reports.totalLiabilitiesAndEquity')}</strong></TableCell>
                                <TableCell align="right"><strong>{formatMoney(bs.totalLiabilitiesAndEquity)}</strong></TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                )}
                {tab === 'ledger' && !accountId && (
                    <Typography sx={{ p: 2 }} color="text.secondary">{t('reports.chooseAccount')}</Typography>
                )}
                {tab === 'ledger' && accountId && ledger && (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('fields.date')}</TableCell>
                                <TableCell>{t('fields.entry')}</TableCell>
                                <TableCell>{t('fields.description')}</TableCell>
                                <TableCell align="right">{t('fields.debit')}</TableCell>
                                <TableCell align="right">{t('fields.credit')}</TableCell>
                                <TableCell align="right">{t('fields.balance')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow>
                                <TableCell colSpan={5}>{t('reports.openingBalance')}</TableCell>
                                <TableCell align="right">{formatMoney(ledger.openingBalance)}</TableCell>
                            </TableRow>
                            {ledger.rows.map((r, i) => (
                                <TableRow key={`${r.entryId}-${i}`}>
                                    <TableCell>{r.date}</TableCell>
                                    <TableCell>
                                        <Link component={RouterLink} to={`/accounting/entries/${r.entryId}`}>{r.entryNumber}</Link>
                                    </TableCell>
                                    <TableCell>{r.memo || r.description}</TableCell>
                                    <TableCell align="right">{r.debit ? formatMoney(r.debit) : ''}</TableCell>
                                    <TableCell align="right">{r.credit ? formatMoney(r.credit) : ''}</TableCell>
                                    <TableCell align="right">{formatMoney(r.runningBalance)}</TableCell>
                                </TableRow>
                            ))}
                            <TableRow>
                                <TableCell colSpan={5}><strong>{t('reports.closingBalance')}</strong></TableCell>
                                <TableCell align="right"><strong>{formatMoney(ledger.closingBalance)}</strong></TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                )}
            </Paper>
        </Box>
    );
};

export default AccountingReports;
